import { useEffect, useRef, useState } from 'preact/hooks';
import { AdCta, AdIcon, AdLabel, type ExternalAdCard } from './formats';

interface ToastProps {
    ad: ExternalAdCard;
    linkRef: { current: HTMLAnchorElement | null };
    /** How long the toast stays up, paused while hovered or focused. */
    duration?: number;
    onClosed: () => void;
}

const reducedMotion = (): boolean =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/** A small card that slides in at the bottom, counts down and slides out again. */
export default function Toast({ ad, linkRef, duration = 7000, onClosed }: ToastProps) {
    const [state, setState] = useState<'entering' | 'shown' | 'leaving'>('entering');
    const bar = useRef<HTMLSpanElement>(null);
    const countdown = useRef<Animation | null>(null);

    useEffect(() => {
        const frame = requestAnimationFrame(() => setState('shown'));
        return () => cancelAnimationFrame(frame);
    }, []);

    useEffect(() => {
        if (state !== 'shown' || !bar.current?.animate) return undefined;
        countdown.current = bar.current.animate(
            [{ transform: 'scaleX(1)' }, { transform: 'scaleX(0)' }],
            { duration, easing: 'linear', fill: 'forwards' },
        );
        countdown.current.finished.then(() => setState('leaving')).catch(() => undefined);
        return () => countdown.current?.cancel();
    }, [state === 'shown']);

    const motion = reducedMotion()
        ? 'transition-opacity duration-300'
        : 'transition-[transform,opacity] duration-500 ease-[cubic-bezier(0.2,0.9,0.3,1.08)]';
    const hidden = state !== 'shown';

    return (
        <div
            role="complementary"
            aria-label="Annons"
            onPointerEnter={() => countdown.current?.pause()}
            onPointerLeave={() => countdown.current?.play()}
            onFocusIn={() => countdown.current?.pause()}
            onFocusOut={() => countdown.current?.play()}
            onTransitionEnd={() => state === 'leaving' && onClosed()}
            class={`fixed inset-x-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] z-[60] flex items-center overflow-hidden rounded-2xl bg-white shadow-[0_0_0_1px_rgb(15_23_42/0.06),0_12px_32px_-8px_rgb(30_0_73/0.28)] md:inset-x-auto md:bottom-6 md:left-6 md:w-[360px] ${motion} ${
                hidden ? 'pointer-events-none opacity-0' : 'opacity-100'
            } ${hidden && !reducedMotion() ? 'translate-y-[calc(100%+2rem)]' : 'translate-y-0'}`}
        >
            <a
                ref={linkRef}
                href={ad.link}
                target="_blank"
                rel="sponsored noopener"
                class="group flex min-w-0 flex-1 items-center gap-3 py-2.5 pl-2.5"
            >
                <AdIcon src={ad.images.icon} size={44} />
                <span class="min-w-0 flex-1 leading-tight">
                    <span class="flex items-baseline gap-2">
                        <b class="font-semibold text-slate-900">{ad.title}</b>
                        <AdLabel />
                    </span>
                    {ad.tagline && (
                        <span class="block truncate text-sm text-slate-500">{ad.tagline}</span>
                    )}
                </span>
                <AdCta>{ad.cta}</AdCta>
            </a>
            <button
                type="button"
                aria-label="Stäng annons"
                onClick={() => setState('leaving')}
                class="mx-1 grid h-8 w-8 shrink-0 place-items-center rounded-full text-xl leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
                &times;
            </button>
            <span
                ref={bar}
                aria-hidden="true"
                class="absolute inset-x-0 bottom-0 h-[3px] origin-left bg-brand-400"
            />
        </div>
    );
}
